import { PRICE_TYPES_MAP, PRICE_TYPES_ORDER, PRICE_STRUCTURE } from '@common/ProductCard/helpers/constants';
import { getPriceSplash } from './splashes';

const handleSort = (
    firstPriceToCompare,
    secondPriceToCompare
) => (PRICE_TYPES_ORDER.indexOf(firstPriceToCompare.type) - PRICE_TYPES_ORDER.indexOf(secondPriceToCompare.type));

const getPricesArray = (prices, isActiveDealsOn) => Object.entries(prices).reduce((acc, priceObject) => {
    const [type, data] = priceObject;

    if (data !== null) {
        if (!isActiveDealsOn && type === PRICE_TYPES_MAP.MDV_PRICE) {
            return acc;
        }

        acc.push({
            type,
            ...data
        });
    }

    return acc;
}, []);

export const parsePrices = ({
    variants,
    cardConfig,
    isActiveDealsOn = false
}) => {
    if (!variants) {
        return null;
    }

    const { hasSimplePrice, hasToIncludeBasePrice } = cardConfig;

    return variants.reduce((pricesObj, variant) => {
        const {
            id: variantId, prices, optin_box: optinBox, special_price: isSpecialPrice
        } = variant;

        const pricesArray = getPricesArray(prices, isActiveDealsOn);

        const sortedPrices = pricesArray.length > 1
            ? [...pricesArray].sort(handleSort)
            : pricesArray;

        const hasToStrikeTrough = (index, type) => {
            const maxStrikeThroughPrices = isActiveDealsOn ? 3 : 2;

            if (type === PRICE_TYPES_MAP.BASE_PRICE && sortedPrices.length > maxStrikeThroughPrices) {
                return false;
            }

            return sortedPrices.length > 1 && index > 0;
        };

        const values = sortedPrices.reduce((acc, price, index) => {
            const { type: priceType } = price;

            const hasToIncludePrice = hasToIncludeBasePrice && priceType === PRICE_TYPES_MAP.BASE_PRICE;

            if ((hasSimplePrice && index > 0) && !hasToIncludePrice) {
                return acc;
            }

            acc.push({
                ...price,
                isPreviousPrice: sortedPrices.length > 1 && index > 0,
                isPriceOnDiscount: isSpecialPrice || (sortedPrices.length > 1 && index === 0),
                hasToStrikeTrough: hasToStrikeTrough(index, price.type),
                isLongPrice: price.type === PRICE_TYPES_MAP.BASE_PRICE && index > 2,
                value: price.formatted_price
            });

            return acc;
        }, []);

        const splash = getPriceSplash(optinBox, isActiveDealsOn, hasSimplePrice);

        pricesObj[variantId] = { values, splash };

        return pricesObj;
    }, {});
};

export const getActualPrice = variant => parsePrices({
    variants: [variant],
    cardConfig: { hasSimplePrice: true, isSpecialPrice: false }
})[variant.id]?.values[0]?.price;

const isValidPriceElement = priceElement => (
    priceElement
        && typeof priceElement.value === 'string'
        && (priceElement.background_color === null || typeof priceElement.background_color === 'string')
        && typeof priceElement.foreground_color === 'string'
        && typeof priceElement.strike_through === 'boolean'
        && ['SMALL', 'MEDIUM', 'LARGE', 'EXTRA_LARGE'].includes(priceElement.text_size)
);

const formatProductPrice = (
    activePrices,
    priceStructure
) => (priceStructure === PRICE_STRUCTURE.SINGLE && isValidPriceElement(activePrices?.[0]?.[0])
    ? [[activePrices[0][0]]]
    : activePrices);

export const getPriceStructure = ({
    cardConfig,
    isNewPriceStructureFeatureActive,
    isActiveDealsOn = false,
    variants
}) => {
    if (variants
        && variants[0].pricing_strategies
        && isNewPriceStructureFeatureActive
    ) {
        const { priceStructure } = cardConfig;

        return variants.reduce((pricesObj, variant) => {
            const {
                id: variantId,
                pricing_strategies: pricingStrategies
            } = variant;

            const { mdv_prices: mdvPrices = [], prices = [] } = pricingStrategies ?? {};

            const activePrice = (isActiveDealsOn && mdvPrices && mdvPrices.length > 0)
                ? mdvPrices : prices;

            if (activePrice) {
                pricesObj[variantId] = formatProductPrice(activePrice, priceStructure);
            }

            return pricesObj;
        }, {});
    }

    return {};
};
